<script setup>
import { t, countLabel, schemaText } from '../lib/i18n.mjs'
import { computed, ref } from 'vue'
import AppIcon from './AppIcon.vue'
import MotifPreview from './MotifPreview.vue'
import TimeFacet from './TimeFacet.vue'
const props = defineProps({
  busy: Boolean,
  schemas: Array,
  links: Array,
  motifs: Array,
  facets: Array,
  activeMotif: String,
  activeFacet: String,
  matchCount: Number,
  timeline: Object,
  timeRange: Array,
  hiddenNodeTypes: Object,
  hiddenLinkTypes: Object,
})
defineEmits([
  'collapse',
  'edit',
  'motif',
  'facet',
  'time',
  'clear',
  'type-visibility',
  'import-schema',
  'export-schema',
])
const hiddenTypes = (kind) => (kind === 'node' ? props.hiddenNodeTypes : props.hiddenLinkTypes)
const query = ref('')
const searchTerm = computed(() => query.value.trim().toLocaleLowerCase())
const visibleFacets = computed(() =>
  props.facets.map((facet) => ({
    ...facet,
    maxCount: facet.entities[0]?.count || 1,
    entities: facet.entities.filter((entity) => entity.term.toLocaleLowerCase().includes(searchTerm.value)),
  })),
)
const totalEntities = computed(() => props.facets.reduce((n, f) => n + f.entities.length, 0))
</script>
<template>
  <aside class="control-panel">
    <section class="control-section motif-section">
      <header class="control-heading">
        <h2>{{ t('论证模式') }} <span class="heading-detail">{{ t('拓扑结构') }}</span></h2>
        <div class="inline-tools">
          <button
            class="icon-button"
            :title="t('自定义拓扑')"
            :aria-label="t('自定义拓扑')"
            @click="$emit('edit', { kind: 'motif' })"
          >
            <AppIcon name="Plus" /></button
          ><button
            class="icon-button collapse-button"
            :title="t('收起交互面板')"
            :aria-label="t('收起交互面板')"
            @click="$emit('collapse')"
          >
            <AppIcon name="PanelLeftClose" />
          </button>
        </div>
      </header>
      <div class="motif-grid">
        <button
          v-for="motif in motifs"
          :key="motif.id"
          class="motif-item"
          :class="{ active: activeMotif === motif.id }"
          :aria-pressed="activeMotif === motif.id"
          @click="$emit('motif', motif.id)"
          @dblclick="$emit('edit', { kind: 'motif', id: motif.id })"
        >
          <MotifPreview :motif="motif" /><span>{{ schemaText(motif, 'name', 'motif') }}</span>
        </button>
      </div>
      <div v-if="activeMotif" class="filter-result">
        <span>{{ countLabel(matchCount >= 250 ? '250+' : matchCount, '个匹配') }}</span
        ><button
          class="icon-button"
          :title="t('清除拓扑筛选')"
          :aria-label="t('清除拓扑筛选')"
          @click="$emit('motif', '')"
        >
          <AppIcon name="X" :size="13" />
        </button>
      </div>
    </section>
    <section class="control-section facet-section">
      <header class="control-heading">
        <h2>
          {{ t('分析切面') }} <span class="heading-detail">{{ t('{0} 个实体', { 0: totalEntities }) }}</span>
        </h2>
        <div class="facet-search">
          <AppIcon name="Search" :size="12" />
          <input
            v-model="query"
            type="search"
            :aria-label="t('检索 Facet 实体')"
            :placeholder="t('检索…')"
            @keydown.esc="query = ''"
          />
          <button
            v-if="query"
            class="facet-search-clear"
            :aria-label="t('清除 Facet 检索')"
            :title="t('清除检索')"
            @click="query = ''"
          >
            <AppIcon name="X" :size="11" />
          </button>
        </div>
        <button
          class="icon-button"
          :title="t('新建实体类别')"
          :aria-label="t('新建实体类别')"
          @click="$emit('edit', { kind: 'facet' })"
        >
          <AppIcon name="Plus" />
        </button>
      </header>
      <div class="facet-grid">
        <TimeFacet :timeline="timeline" :range="timeRange" @change="$emit('time', $event)" />
        <div class="facet-attributes">
          <div v-for="facet in visibleFacets" :key="facet.id" class="facet-column">
            <button
              class="facet-title"
              :title="
                t('编辑 {0} · {1}', {
                  0: schemaText(facet, 'name', 'facet'),
                  1: t('结构化属性'),
                })
              "
              @click="$emit('edit', { kind: 'facet', id: facet.id })"
            >
              {{ schemaText(facet, 'name', 'facet') }}<AppIcon name="Pencil" :size="11" />
            </button>
            <div class="facet-entries">
              <button
                v-for="entity in facet.entities"
                :key="entity.term"
                class="entity"
                :class="{ active: activeFacet === `${facet.id}:${entity.term}` }"
                :title="t('{0} 次出现，{1} 个论证单元', { 0: entity.count, 1: entity.unitIds.length })"
                @click="$emit('facet', { facetId: facet.id, ...entity })"
              >
                <span class="entity-label">{{ entity.term }}</span
                ><span class="entity-stat"
                  ><i :style="{ width: `${Math.max(6, (entity.count / facet.maxCount) * 100)}%` }" /><small>{{
                    entity.count
                  }}</small></span
                ></button
              ><span v-if="!facet.entities.length" class="muted small">{{
                searchTerm ? t('无匹配') : t('暂无实体')
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="activeFacet" class="filter-result">
        <span>{{ activeFacet.split(':').slice(1).join(':') }}</span
        ><button
          class="icon-button"
          :title="t('清除实体筛选')"
          :aria-label="t('清除实体筛选')"
          @click="$emit('facet', null)"
        >
          <AppIcon name="X" :size="13" />
        </button>
      </div>
    </section>
    <section class="control-section schema-section">
      <header class="control-heading">
        <h2>{{ t('分类体系') }} <span class="heading-detail">{{ t('本体定义') }}</span></h2>
        <div class="inline-tools">
          <button
            class="icon-button"
            :disabled="busy"
            :aria-label="t('导入 Schema')"
            :title="t('导入 Schema')"
            @click="$emit('import-schema')"
          >
            <AppIcon name="Upload" :size="15" />
          </button>
          <button
            class="icon-button"
            :aria-label="t('导出 Schema')"
            :title="t('导出 Schema')"
            @click="$emit('export-schema')"
          >
            <AppIcon name="Download" :size="15" />
          </button>
        </div>
      </header>
      <div
        v-for="group in [
          { key: 'node', title: 'Node', items: schemas },
          { key: 'link', title: 'Link', items: links },
        ]"
        :key="group.key"
        class="schema-group"
      >
        <header class="schema-heading">
          <h3>{{ group.title }}</h3>
          <button
            class="icon-button"
            :title="group.key === 'node' ? t('新建节点类型') : t('新建关系类型')"
            :aria-label="group.key === 'node' ? t('新建节点类型') : t('新建关系类型')"
            @click="$emit('edit', { kind: group.key })"
          >
            <AppIcon name="Plus" :size="15" />
          </button>
        </header>
        <div class="schema-table">
          <div class="schema-row schema-labels schema-column-labels">
            <span>{{ t('名称') }}</span
            ><span>{{ t('定义') }}</span
            ><span>{{ t('样式') }}</span>
          </div>
          <div v-for="item in group.items" :key="item.id" class="schema-option">
            <input
              type="checkbox"
              class="schema-checkbox"
              :aria-label="
                t('显示{0}类型：{1}', {
                  0: group.key === 'node' ? t('节点') : t('关系'),
                  1: schemaText(item, 'name', group.key),
                })
              "
              :checked="!hiddenTypes(group.key)?.has(item.id)"
              @change="
                $emit('type-visibility', { kind: group.key, id: item.id, visible: $event.target.checked })
              "
            />
            <button
              class="schema-row schema-entry"
              :data-schema-id="item.id"
              :title="schemaText(item, 'definition', group.key)"
              @click="$emit('edit', { kind: group.key, id: item.id })"
            >
              <span class="schema-name">{{ schemaText(item, 'name', group.key) }}</span>
              <span class="schema-definition">{{ schemaText(item, 'definition', group.key) }}</span
              ><span class="schema-style"
                ><i
                  v-if="group.key === 'node'"
                  class="color-swatch"
                  :style="{ background: item.color }"
                /><svg v-else width="23" height="12">
                  <path
                    d="M1 6 H22"
                    :stroke="item.color"
                    stroke-width="2"
                    :stroke-dasharray="
                      item.dash === 'dashed' ? '6 3' : item.dash === 'dotted' ? '2 3' : undefined
                    "
                  /></svg
                ><small>{{ item.color }}</small></span
              >
            </button>
          </div>
        </div>
      </div>
    </section>
    <footer class="control-footer"><span>ArguLoom</span><span>{{ t('工作区') }} / 01</span></footer>
  </aside>
</template>
